import _ from 'lodash';
import moment from 'moment';
import { action, observable, computed, makeObservable } from 'mobx';
import { notificationError, translate } from 'core/utils';
import { BaseViewStore } from 'core/stores';
import FulfillmentOrderProcessingStore from './FulfillmentOrderProcessingStore';
import ShipmentDocumentStore from './ShipmentDocumentStore';
import { UploadDocumentForm } from 'modules/order/forms';
import { BaasicDropdownViewStore } from 'common/stores';
import { CarrierServiceStore, CarrierStore } from 'modules/carrier/stores';
import { BillOfLadingStore } from 'modules/bill-of-lading/stores';
import { shipmentDocumentTypeEnum, shipmentDocumentSourceEnum } from 'common/constants';

// Dropdown values are strings, not the numeric ShipmentDocumentType: BaasicDropdownViewStore.getOption
// drops falsy values, so a packing slip (0) would never stick. shipmentDocumentType is the value the
// API expects, and is null for the shipping label, which is an assignment rather than a stored document.
const documentTypes = {
	shippingLabel: {
		value: 'shippingLabel',
		label: 'FULFILLMENTORDER.DOCUMENTTYPE.SHIPPINGLABEL',
		shipmentDocumentType: null,
	},
	packingSlip: {
		value: 'packingSlip',
		label: 'FULFILLMENTORDER.DOCUMENTTYPE.PACKINGSLIP',
		shipmentDocumentType: shipmentDocumentTypeEnum.packingSlip,
	},
	upcLabel: {
		value: 'upcLabel',
		label: 'FULFILLMENTORDER.DOCUMENTTYPE.UPCLABEL',
		shipmentDocumentType: shipmentDocumentTypeEnum.upcLabel,
	},
	gs1Label: {
		value: 'gs1Label',
		label: 'FULFILLMENTORDER.DOCUMENTTYPE.GS1LABEL',
		shipmentDocumentType: shipmentDocumentTypeEnum.gs1Label,
	},
	billOfLading: {
		value: 'billOfLading',
		label: 'FULFILLMENTORDER.DOCUMENTTYPE.BILLOFLADING',
		shipmentDocumentType: shipmentDocumentTypeEnum.billOfLading,
	},
};

/**
 * Backs the single Upload Document modal that replaced the separate Upload Label and Upload BOL modals.
 * The caller decides which document types this fulfillment order may receive; unavailable types are
 * left out of the dropdown rather than shown disabled.
 */
class UploadDocumentViewStore extends BaseViewStore {
	@observable existingDocuments = [];

	form = new UploadDocumentForm({
		onSuccess: async () => {
			await this.upload();
		},
		onError: async () => {
			if (!this.form.$('file').value) {
				this.rootStore.notificationStore.error('MESSAGES.REQUIRED.FULFILLMENTORDER.UPLOADDOCUMENTMODAL.FILE');
			}
		},
	});

	constructor(rootStore, { modalParams }) {
		super(rootStore);
		makeObservable(this);
		this.modalParams = modalParams;
		this.fulfillmentOrderProcessingStore = new FulfillmentOrderProcessingStore(rootStore);
		this.shipmentDocumentStore = new ShipmentDocumentStore(rootStore);
		this.billOfLadingStore = new BillOfLadingStore(rootStore);
		this.initialize();
	}

	@computed get selectedDocumentType() {
		return documentTypes[this.form.$('documentType').value];
	}

	@computed get isShippingLabel() {
		return _.get(this.selectedDocumentType, 'value') === documentTypes.shippingLabel.value;
	}

	@computed get isBillOfLading() {
		return _.get(this.selectedDocumentType, 'value') === documentTypes.billOfLading.value;
	}

	/**
	 * The tracking/carrier/service/cost block. A shipping label always needs it, and so does the BOL of an
	 * order that won an LTL rate off a regular channel - it is shipped like any other shipment, only the
	 * document is stored as a bill of lading. Designated LTL Freight orders upload the document alone.
	 */
	@computed get showShipmentDetails() {
		return (
			this.isShippingLabel ||
			(this.isBillOfLading && this.modalParams.data.billOfLadingRequiresShipmentDetails === true)
		);
	}

	/**
	 * "12/05/2026 · Uploaded via API" for the selected type, so overwriting is a deliberate act.
	 */
	@computed get existingDocumentDescription() {
		const type = _.get(this.selectedDocumentType, 'shipmentDocumentType');
		if (type == null) {
			return null;
		}

		const existing = this.existingDocuments.find(o => o.type === type);
		if (!existing) {
			return null;
		}

		const source = _.find(shipmentDocumentSourceEnum, o => o.value === existing.source);
		return `${moment(existing.dateCreated).format('MM/DD/YYYY')}${source ? ` · ${source.label}` : ''}`;
	}

	@action.bound
	initialize = async () => {
		this.suspend();

		this.documentTypeDropdownStore = new BaasicDropdownViewStore();
		this.carrierServiceDropdownStore = new BaasicDropdownViewStore();
		this.carrierDropdownStore = new BaasicDropdownViewStore();

		const options = this.getDocumentTypeOptions();

		await Promise.all([
			this.documentTypeDropdownStore.initialize({
				field: this.form.$('documentType'),
				staticOptions: options,
				onChangeCallback: () => this.applyDocumentTypeRules(),
			}),
			this.carrierServiceDropdownStore.initialize({
				field: this.form.$('carrierServiceId'),
				store: new CarrierServiceStore(this.rootStore),
				fetchAll: true,
				defaultInitialValue: { path: 'value', value: this.modalParams.data.assignedCarrierServiceId },
			}),
			this.carrierDropdownStore.initialize({
				field: this.form.$('carrierId'),
				fetchAll: true,
				store: new CarrierStore(this.rootStore),
				filterProperties: {
					sellerIds: [this.modalParams.data.sellerId],
				},
				defaultInitialValue: { path: 'value', value: this.modalParams.data.assignedCarrierId },
			}),
		]);

		if (options.length === 1) {
			await this.documentTypeDropdownStore.setValue(options[0]);
		}
		this.applyDocumentTypeRules();

		await this.loadExistingDocuments();

		this.resume();
	};

	getDocumentTypeOptions = () => {
		const { canUploadShippingLabel, canUploadBillOfLading, canUploadDocument } = this.modalParams.data;
		const types = [];

		if (canUploadShippingLabel) {
			types.push(documentTypes.shippingLabel);
		}
		if (canUploadDocument) {
			types.push(documentTypes.packingSlip);
			types.push(documentTypes.upcLabel);
			types.push(documentTypes.gs1Label);
		}
		if (canUploadBillOfLading) {
			types.push(documentTypes.billOfLading);
		}

		// BaasicDropdown renders option labels as-is, so they have to be translated up front.
		return types.map(type => ({ ...type, label: translate(type.label) }));
	};

	@action.bound
	applyDocumentTypeRules = () => {
		const rules = this.showShipmentDetails ? 'required' : '';
		this.form.$('trackingNumber').set('rules', rules);
		this.form.$('carrierServiceId').set('rules', rules);
		this.form.$('carrierId').set('rules', rules);
		// For LTL the tracking number is the PRO number. The label also drives the required message, hence
		// MESSAGES.REQUIRED.FULFILLMENTORDER.UPLOADBOLMODAL.PRONUMBER.
		const trackingNumberLabel = this.isBillOfLading
			? 'FULFILLMENTORDER.UPLOADBOLMODAL.PRONUMBER'
			: 'FULFILLMENTORDER.UPLOADLABELMODAL.TRACKINGNUMBER';
		this.form.$('trackingNumber').set('label', trackingNumberLabel);
	};

	@action.bound
	loadExistingDocuments = async () => {
		try {
			const documents = await this.shipmentDocumentStore.getForFulfillmentOrder(this.modalParams.data.selectedItemId);
			this.existingDocuments = documents || [];
		} catch (err) {
			// The hint is informational only - a failed lookup must not block the upload.
			this.existingDocuments = [];
		}
	};

	@action.bound
	saveFile = async files => {
		if (files.length > 1) {
			this.rootStore.notificationStore.error('FULFILLMENTORDER.UPLOADDOCUMENTMODAL.MULTIPLEFILESERROR');
		} else {
			this.form.$('file').set('value', { content: files[0], fileName: files[0].name });
		}
	};

	@action.bound
	upload = async () => {
		try {
			this.suspend();

			if (this.isShippingLabel) {
				await this.uploadShippingLabel();
			} else if (this.isBillOfLading) {
				await this.uploadBillOfLading();
			} else {
				await this.uploadShipmentDocument();
			}

			this.rootStore.notificationStore.success(['FULFILLMENTORDER.UPLOADDOCUMENTMODAL.SUCCESS']);
			this.modalParams.success();
		} catch (err) {
			if (err.statusCode == 409) {
				const fallbackKey = this.isBillOfLading
					? 'FULFILLMENTORDER.UPLOADBOLMODAL.MIXEDCARRIERSERVICE'
					: 'FULFILLMENTORDER.UPLOADLABELMODAL.MIXEDCARRIERSERVICE';
				this.rootStore.notificationStore.error([err.data || fallbackKey]);
			} else {
				notificationError({ store: this.rootStore.notificationStore, err });
			}
		} finally {
			this.resume();
		}
	};

	buildFormData = () => {
		const formData = new FormData();
		formData.append('file', this.form.$('file').value.content);

		if (this.showShipmentDetails) {
			const { trackingNumber, carrierServiceId, carrierId, shippingCost } = this.form.values();
			formData.append('data', JSON.stringify({ trackingNumber, carrierServiceId, carrierId, shippingCost }));
		}

		return formData;
	};

	uploadShippingLabel = async () => {
		await this.fulfillmentOrderProcessingStore.uploadLabel(this.modalParams.data.selectedItemId, this.buildFormData());
	};

	// Without the "data" part the API stores the document alone, which is what the pull-based LTL Freight
	// orders do; with it the upload also records the shipment.
	uploadBillOfLading = async () => {
		await this.billOfLadingStore.upload(this.modalParams.data.selectedItemId, this.buildFormData());
	};

	uploadShipmentDocument = async () => {
		const formData = new FormData();
		formData.append('file', this.form.$('file').value.content);
		await this.shipmentDocumentStore.uploadDocument(
			this.modalParams.data.selectedItemId,
			this.selectedDocumentType.shipmentDocumentType,
			formData
		);
	};
}

export default UploadDocumentViewStore;
